﻿import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { PermissionGuard } from '../../components/PermissionGuard'
import { Heading, Text, Button, Input, Select, SortableTableHeader } from '../../components/ui'
import { userLogsService, type LogWithUser, type LogAction, type EntityType } from '../../services/userLogs'
import { supabase } from '../../lib/supabase'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../../constants'
import PasiGptHistoricoTab from './PasiGptHistoricoTab'

const ACTIONS: LogAction[] = ['create', 'update', 'delete', 'view', 'export', 'import', 'login', 'logout', 'other']
const ENTITY_TYPES: EntityType[] = [
  'user',
  'user_group',
  'organization',
  'department',
  'user_permissions',
  'permission',
  'module',
  'parameter',
  'quality_category',
  'quality_incident',
  'quality_incident_stage',
  'quality_incident_type',
  'quality_incident_origin',
  'quality_improcedent_reason',
  'email_template',
  'superapp_dashboard_milestone',
  'social_post',
  'social_post_comment',
  'ia_prompts_agente',
  'ia_n8n_flow',
  'ia_base_conhecimento',
  'ia_documento',
  'informativo_boletim',
  'process_flowchart',
  'marketing_clipping',
  'marketing_clipping_vehicle',
  'marketing_clipping_category',
  'marketing_clipping_veiculo',
  'marketing_clipping_categoria',
  'marketing_clipping_search_site',
  'marketing_clipping_search_config',
  'marketing_clipping_ia_config',
  'marketing_news_alert',
  'marketing_game',
  'marketing_game_session',
  'support_center_appointment',
  'support_center_appointment_attachment',
  'support_center_triage',
  'support_center_specialty',
  'support_center_specialty_user',
  'support_center_professional_role',
  'support_center_user_unavailability',
  'support_center_holiday',
  'support_center_assistance',
  'support_center_cancel_reason',
  'support_center_referral_origin',
  'support_center_demand_type',
  'marketing_stock_item',
  'marketing_stock_item_photo',
  'marketing_stock_category',
  'marketing_stock_supplier',
  'supplier',
  'payment_method',
  'marketing_stock_location',
  'marketing_stock_movement',
  'marketing_request',
  'marketing_request_stage',
  'marketing_request_attachment',
  'supplier_quote',
  'contract',
  'contract_amount',
  'contract_attachment',
  'other',
]

const getLastMonthDates = () => {
  const hoje = new Date()
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, hoje.getDate())
  const formatDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  return { inicio: formatDate(inicio), fim: formatDate(fim) }
}

/** Filtro por data do calendário local: início do dia 00:00:00.000 (evita deslocamento UTC de YYYY-MM-DD). */
function localCalendarDayStartIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return new Date(yyyyMmDd).toISOString()
  }
  const [y, m, d] = parts
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString()
}

/** Fim do mesmo dia civil local: 23:59:59.999 (inclui todos os registros daquele dia). */
function localCalendarDayEndIso(yyyyMmDd: string): string {
  const parts = yyyyMmDd.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    const t = new Date(yyyyMmDd)
    t.setHours(23, 59, 59, 999)
    return t.toISOString()
  }
  const [y, m, d] = parts
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString()
}

const ConfiguracoesLogs: React.FC = () => {
  const lastMonthDates = getLastMonthDates()

  const [logs, setLogs] = useState<LogWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)

  // Filtros — datas iniciais = último mês (mesmo dia, mês anterior → hoje)
  const [userIdFilter, setUserIdFilter] = useState<string>('')
  const [actionFilter, setActionFilter] = useState<LogAction | 'all'>('all')
  const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType | 'all'>('all')
  const [dateFrom, setDateFrom] = useState<string>(lastMonthDates.inicio)
  const [dateTo, setDateTo] = useState<string>(lastMonthDates.fim)
  const [searchTerm, setSearchTerm] = useState<string>('')
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // Ordenação
  const [sortBy, setSortBy] = useState<'created_at' | 'user_name' | 'action' | 'entity_type' | 'description'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Modal de detalhes
  const [selectedLog, setSelectedLog] = useState<LogWithUser | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  
  // Lista de usuários para o filtro
  const [users, setUsers] = useState<Array<{ id: string; name: string | null }>>([])

  const [activeTab, setActiveTab] = useState<'atividades' | 'pasiGpt'>('atividades')

  // Carregar usuários para o filtro
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name')
          .order('name')
          .limit(100)
        
        if (error) throw error
        setUsers(data || [])
      } catch (err) {
        console.error('Erro ao carregar usuários:', err)
      }
    }
    loadUsers()
  }, [])

  const loadLogs = useCallback(
    async (pageOverride?: number) => {
      const page = pageOverride ?? currentPage
      try {
        setLoading(true)
        setError(null)

        const filters: Record<string, unknown> = {
          page,
          limit: itemsPerPage,
        }

        if (userIdFilter) {
          filters.userId = userIdFilter
        }

        if (actionFilter !== 'all') {
          filters.action = actionFilter
        }

        if (entityTypeFilter !== 'all') {
          filters.entityType = entityTypeFilter
        }

        if (dateFrom) {
          filters.dateFrom = localCalendarDayStartIso(dateFrom)
        }

        if (dateTo) {
          filters.dateTo = localCalendarDayEndIso(dateTo)
        }

        if (searchTerm) {
          filters.search = searchTerm
        }

        const result = await userLogsService.getLogs(filters)
        setLogs(result.logs)
        setTotal(result.total)
      } catch (err: any) {
        console.error('Erro ao carregar logs:', err)
        setError(err?.message || 'Erro ao carregar logs')
      } finally {
        setLoading(false)
      }
    },
    [
      currentPage,
      itemsPerPage,
      userIdFilter,
      actionFilter,
      entityTypeFilter,
      dateFrom,
      dateTo,
      searchTerm,
    ]
  )

  const handleBuscar = () => {
    setHasSearched(true)
    setCurrentPage(1)
    setError(null)
    loadLogs(1)
  }

  const handleLimparFiltros = () => {
    const dates = getLastMonthDates()
    setUserIdFilter('')
    setActionFilter('all')
    setEntityTypeFilter('all')
    setDateFrom(dates.inicio)
    setDateTo(dates.fim)
    setSearchTerm('')
    setCurrentPage(1)
    setHasSearched(false)
    setLogs([])
    setTotal(0)
    setError(null)
  }

  // Função para alternar ordenação
  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }


  // Abrir modal de detalhes
  const openDetailModal = (log: LogWithUser) => {
    setSelectedLog(log)
    setIsDetailModalOpen(true)
  }

  // Fechar modal de detalhes
  const closeDetailModal = () => {
    setSelectedLog(null)
    setIsDetailModalOpen(false)
  }

  // Formatação de data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  // Tradução de ações
  const translateAction = (action: LogAction) => {
    const translations: Record<LogAction, string> = {
      create: 'Criar',
      update: 'Atualizar',
      delete: 'Excluir',
      view: 'Visualizar',
      export: 'Exportar',
      import: 'Importar',
      login: 'Login',
      logout: 'Logout',
      other: 'Outro',
    }
    return translations[action] || action
  }

  // Tradução de tipos de entidade
  const translateEntityType = (entityType: EntityType) => {
    const translations: Record<EntityType, string> = {
      user: 'Usuário',
      user_group: 'Grupo',
      organization: 'Organização',
      department: 'Departamento',
      user_permissions: 'Permissões do Usuário',
      permission: 'Permissão',
      module: 'Módulo',
      parameter: 'Parâmetro',
      quality_category: 'Qualidade - categoria',
      quality_incident: 'Qualidade - ticket',
      quality_incident_stage: 'Qualidade - etapa (Kanban)',
      quality_incident_type: 'Qualidade - motivo',
      quality_incident_origin: 'Qualidade - origem do ticket',
      quality_improcedent_reason: 'Qualidade - registo improcedente',
      email_template: 'Configurações - modelo de e-mail',
      superapp_dashboard_milestone: 'Superapp Dashboard - momento-chave',
      social_post: 'Feed social - postagem',
      social_post_comment: 'Feed social - comentário',
      ia_prompts_agente: 'IA - prompt de agente',
      ia_n8n_flow: 'IA - fluxo N8N',
      ia_base_conhecimento: 'IA - base de conhecimento',
      ia_documento: 'IA - documento',
      informativo_boletim: 'Informativo - boletim',
      process_flowchart: 'Qualidade - fluxograma',
      marketing_clipping: 'Marketing - clipping',
      marketing_clipping_vehicle: 'Marketing - veículo de clipping',
      marketing_clipping_category: 'Marketing - categoria de clipping',
      marketing_clipping_veiculo: 'Marketing - veículo de clipping',
      marketing_clipping_categoria: 'Marketing - categoria de clipping',
      marketing_clipping_search_site: 'Marketing - site de busca automática',
      marketing_clipping_search_config: 'Marketing - config de busca automática',
      marketing_clipping_ia_config: 'Marketing - config análise de IA',
      marketing_news_alert: 'Alerta de notícia',
      marketing_game: 'Marketing - jogo de caça-palavras',
      marketing_game_session: 'Marketing - sessão de caça-palavras',
      support_center_appointment: 'Central de Amparo - agendamento',
      support_center_appointment_attachment: 'Central de Amparo - anexo do agendamento',
      support_center_triage: 'Central de Amparo - triagem',
      support_center_specialty: 'Central de Amparo - especialidade',
      support_center_specialty_user: 'Central de Amparo - especialidade do profissional',
      support_center_professional_role: 'Central de Amparo - função do profissional',
      support_center_user_unavailability: 'Central de Amparo - indisponibilidade',
      support_center_holiday: 'Central de Amparo - feriado',
      support_center_assistance: 'Central de Amparo - assistência',
      support_center_form: 'Central de Amparo - formulário',
      support_center_cancel_reason: 'Central de Amparo - motivo de cancelamento',
      support_center_referral_origin: 'Central de Amparo - origem do encaminhamento',
      support_center_demand_type: 'Central de Amparo - tipo de demanda',
      marketing_stock_item: 'Estoque - item',
      marketing_stock_item_photo: 'Estoque - foto do item',
      marketing_stock_category: 'Estoque - categoria',
      marketing_stock_supplier: 'Estoque - fornecedor',
      supplier: 'Fornecedor',
      payment_method: 'Forma de pagamento',
      payment_source: 'Fonte pagadora',
      marketing_stock_location: 'Estoque - local',
      marketing_stock_movement: 'Estoque - movimentação',
      marketing_request: 'Marketing - solicitação',
      marketing_request_stage: 'Marketing - etapa de solicitação',
      marketing_request_attachment: 'Marketing - anexo de solicitação',
      contract: 'Marketing - contrato',
      contract_category: 'Marketing - categoria de contrato',
      contract_amount: 'Marketing - valor de contrato',
      contract_attachment: 'Marketing - anexo de contrato',
      marketing_invoice: 'Marketing - nota fiscal',
      supplier_quote: 'Marketing - orçamento de fornecedor',
      marketing_payment: 'Marketing - pagamento',
      marketing_campaign: 'Marketing - campanha',
      marketing_event: 'Marketing - evento',
      marketing_material: 'Marketing - material',
      marketing_report: 'Marketing - relatório',
      marketing_expense: 'Marketing - despesa',
      cct_buscar_dados: 'CCT - buscar dados',
      other: 'Outro',
    }
    return translations[entityType] || entityType
  }

  // Ordenar logs client-side
  const sortedLogs = useMemo(() => {
    const copy = [...logs]
    return copy.sort((a, b) => {
      let aValue: any
      let bValue: any

      switch (sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at).getTime()
          bValue = new Date(b.created_at).getTime()
          break
        case 'user_name':
          aValue = (a.profiles?.name || '').toLowerCase()
          bValue = (b.profiles?.name || '').toLowerCase()
          break
        case 'action':
          aValue = translateAction(a.action).toLowerCase()
          bValue = translateAction(b.action).toLowerCase()
          break
        case 'entity_type':
          aValue = translateEntityType(a.entity_type).toLowerCase()
          bValue = translateEntityType(b.entity_type).toLowerCase()
          break
        case 'description':
          aValue = (a.description || '').toLowerCase()
          bValue = (b.description || '').toLowerCase()
          break
        default:
          aValue = a.created_at
          bValue = b.created_at
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, sortBy, sortOrder])

  // Resetar página quando ordenação mudar
  useEffect(() => {
    setCurrentPage(1)
  }, [sortBy, sortOrder])

  const totalPages = Math.ceil(total / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(currentPage * itemsPerPage, total)

  const handlePreviousPage = () => {
    if (currentPage > 1 && hasSearched) {
      const p = currentPage - 1
      setCurrentPage(p)
      loadLogs(p)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages && hasSearched) {
      const p = currentPage + 1
      setCurrentPage(p)
      loadLogs(p)
    }
  }

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && hasSearched) {
      setCurrentPage(page)
      loadLogs(page)
    }
  }

  return (
    <>
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard 
          moduleName="configuracoes_logs" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                Logs de Atividade
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {activeTab === 'atividades'
                  ? 'Visualize o histórico de ações realizadas pelos usuários no sistema.'
                  : 'Histórico de conversas do PasiGPT, com filtro por usuário.'}
              </Text>
            </div>

            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('atividades')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'atividades'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Atividades
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('pasiGpt')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'pasiGpt'
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  PasiGPT
                </button>
              </nav>
            </div>

            {activeTab === 'atividades' && (
              <>
            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <div className="flex items-center mb-4">
                <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  Filtros
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Busca por descrição */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Buscar na descrição
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Digite para buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  </div>
                </div>

                {/* Filtro por usuário */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Usuário
                  </label>
                  <Select
                    value={userIdFilter}
                    onChange={(e) => setUserIdFilter(e.target.value)}
                  >
                    <option value="">Todos os usuários</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.id}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Filtro por ação */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ação
                  </label>
                  <Select
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value as LogAction | 'all')}
                  >
                    <option value="all">Todas as ações</option>
                    {ACTIONS.map((action) => (
                      <option key={action} value={action}>
                        {translateAction(action)}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Filtro por tipo de entidade */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de Entidade
                  </label>
                  <Select
                    value={entityTypeFilter}
                    onChange={(e) => setEntityTypeFilter(e.target.value as EntityType | 'all')}
                  >
                    <option value="all">Todos os tipos</option>
                    {ENTITY_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {translateEntityType(type)}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Filtro por data inicial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Inicial
                  </label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>

                {/* Filtro por data final */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Data Final
                  </label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  variant="solid"
                  size="sm"
                  onClick={handleBuscar}
                  disabled={loading}
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLimparFiltros}
                  disabled={loading}
                >
                  <XMarkIcon className="h-4 w-4 mr-2 inline" />
                  Limpar Filtros
                </Button>
              </div>
            </div>

            {/* Tabela de logs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <Text color="muted">Carregando logs...</Text>
                </div>
              ) : error ? (
                <div className="p-8 text-center">
                  <Text className="text-red-600 dark:text-red-400">{error}</Text>
                </div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center">
                  <Text color="muted">
                    {!hasSearched
                      ? 'Ajuste os filtros e clique em Buscar para carregar os registros.'
                      : 'Nenhum log encontrado.'}
                  </Text>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <SortableTableHeader 
                            sortKey="created_at" 
                            currentSort={sortBy} 
                            sortOrder={sortOrder} 
                            onSort={toggleSort}
                          >
                            Data/Hora
                          </SortableTableHeader>
                          <SortableTableHeader 
                            sortKey="user_name" 
                            currentSort={sortBy} 
                            sortOrder={sortOrder} 
                            onSort={toggleSort}
                          >
                            Usuário
                          </SortableTableHeader>
                          <SortableTableHeader 
                            sortKey="action" 
                            currentSort={sortBy} 
                            sortOrder={sortOrder} 
                            onSort={toggleSort}
                          >
                            Ação
                          </SortableTableHeader>
                          <SortableTableHeader 
                            sortKey="entity_type" 
                            currentSort={sortBy} 
                            sortOrder={sortOrder} 
                            onSort={toggleSort}
                          >
                            Tipo
                          </SortableTableHeader>
                          <SortableTableHeader 
                            sortKey="description" 
                            currentSort={sortBy} 
                            sortOrder={sortOrder} 
                            onSort={toggleSort}
                          >
                            Descrição
                          </SortableTableHeader>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {formatDate(log.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {log.profiles?.name || 'Usuário desconhecido'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200">
                                {translateAction(log.action)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              {translateEntityType(log.entity_type)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                              {log.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetailModal(log)}
                                className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                Detalhes
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {startIndex + 1} a {endIndex} de {total} registros
                          </Text>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>
                          
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber: number
                              if (totalPages <= 5) {
                                pageNumber = i + 1
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i
                              } else {
                                pageNumber = currentPage - 2 + i
                              }
                              
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    currentPage === pageNumber
                                      ? 'bg-primary-600 text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              )
                            })}
                          </div>
                          
                          <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
              </>
            )}

            {activeTab === 'pasiGpt' && <PasiGptHistoricoTab />}
          </div>
        </PermissionGuard>
      </main>

      {/* Modal de Detalhes */}
      {isDetailModalOpen && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                Detalhes do Log
              </Heading>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeDetailModal}
                className="p-2"
              >
                <XMarkIcon className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Data/Hora
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {formatDate(selectedLog.created_at)}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Usuário
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {selectedLog.profiles?.name || 'Usuário desconhecido'}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Ação
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {translateAction(selectedLog.action)}
                </Text>
              </div>

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Tipo de Entidade
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {translateEntityType(selectedLog.entity_type)}
                </Text>
              </div>

              {selectedLog.entity_id && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    ID da Entidade
                  </Text>
                  <Text className="text-gray-900 dark:text-white font-mono text-sm">
                    {selectedLog.entity_id}
                  </Text>
                </div>
              )}

              <div>
                <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                  Descrição
                </Text>
                <Text className="text-gray-900 dark:text-white">
                  {selectedLog.description}
                </Text>
              </div>

              {selectedLog.page_url && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    URL da Página
                  </Text>
                  <Text className="text-gray-900 dark:text-white break-all">
                    {selectedLog.page_url}
                  </Text>
                </div>
              )}

              {selectedLog.metadata && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    Metadados
                  </Text>
                  <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded text-sm overflow-x-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.user_agent && (
                <div>
                  <Text size="sm" className="text-gray-500 dark:text-gray-400 mb-1">
                    User Agent
                  </Text>
                  <Text className="text-gray-900 dark:text-white text-sm break-all">
                    {selectedLog.user_agent}
                  </Text>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={closeDetailModal}
                style={{ backgroundColor: BRAND_COLOR }}
                className="w-full"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ConfiguracoesLogs
